/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-constant-binary-expression */

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { CognitoIdentityProviderClient,VerifySoftwareTokenCommand, InitiateAuthCommand, GetUserCommand, AuthFlowType, GlobalSignOutCommand, InitiateAuthCommandInput, ForgotPasswordCommand, ConfirmForgotPasswordCommand, RespondToAuthChallengeCommand, AssociateSoftwareTokenCommand, SetUserMFAPreferenceCommand } from "@aws-sdk/client-cognito-identity-provider";
import config from "../config/config.json";
import { jwtDecode, JwtPayload } from "jwt-decode";

export const cognitoClient = new CognitoIdentityProviderClient({
  region: config.region,
});
type AuthFlowLocal = AuthFlowType;
const authFlow: AuthFlowLocal = "USER_PASSWORD_AUTH";
interface User {
  username: string;
  attributes: {
    email: string;
    [key: string]: any;
  };
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  signIn: (username: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  verifyMfaCodeForSetup: (username: string, code: string) => Promise<void>;
  mfaRequired: boolean;
  verifyMfaCodeForLogin: (username: string, code: string) => Promise<void>;
  resetPassword: (username: string, resetCode: string, newPassword: string) => Promise<void>;
  changePasswordBySession: (username: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const API_URL = import.meta.env.VITE_API_URL;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [mfaRequired, setMfaRequired] = useState<boolean>(false);
  const [mfaSession, setMfaSession] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAuthState();
  }, []);

  async function checkAuthState() {
    try {
      const storedUser = localStorage.getItem("authenticatedUser");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      }
    } catch (error) {
      // Handle error
      console.error("Error checking auth state:", error);
    }
    setIsLoading(false);
  }

  async function signIn(username: string, password: string) {
    try {
      console.log("Starting sign-in process...");

      const command = new InitiateAuthCommand({
        AuthFlow: authFlow,
        ClientId: config.clientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      });

      console.log("Sending InitiateAuthCommand...");
      const response = await cognitoClient.send(command);
      console.log("InitiateAuthCommand response:", response);

      if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        console.log("New password is required.");
        setSession(response.Session || null);
        return {
          ChallengeName: "NEW_PASSWORD_REQUIRED",
          Session: response.Session, // Return the session for the new password flow
        };
      } else if (response.ChallengeName === "MFA_SETUP") {
        console.log("MFA setup is required.");
        setAccessToken(response.AuthenticationResult?.AccessToken || null);
        setChallenge("MFA_SETUP");
        const associateCommand = new AssociateSoftwareTokenCommand({
          Session: response.Session,
        });
        const associateResponse = await cognitoClient.send(associateCommand);
        console.log("AssociateSoftwareToken response:", associateResponse);

        setMfaSession(associateResponse.Session || null);
        return {
          ChallengeName: "MFA_SETUP",
          Session: associateResponse.Session,
          SecretCode: associateResponse.SecretCode,
        };
      } else if (response.ChallengeName === "SOFTWARE_TOKEN_MFA") {
        console.log("MFA is required.");
        setChallenge("SOFTWARE_TOKEN_MFA");
        setMfaRequired(true);
        setMfaSession(response.Session || null);
        return {
          ChallengeName: "SOFTWARE_TOKEN_MFA",
          Session: response.Session,
        };
      } else if (response.AuthenticationResult) {
        console.log("Authentication successful. Storing tokens...");
        sessionStorage.setItem("idToken", response.AuthenticationResult.IdToken || '');
        sessionStorage.setItem("accessToken", response.AuthenticationResult.AccessToken || '');
        sessionStorage.setItem("refreshToken", response.AuthenticationResult.RefreshToken || '');

        const userName = await getUserName(response.AuthenticationResult.AccessToken);
        console.log("Fetched username:", userName);
        sessionStorage.setItem("user_id", userName || '');

        const userAttr: any = await fetchUser(userName);
        console.log("Fetched user attributes:", userAttr);
        const dashboards = await getAllEmbeddedDashboards();

        sessionStorage.setItem("given_name", userAttr?.FirstName || '');
        sessionStorage.setItem("family_name", userAttr?.LastName || '');
        sessionStorage.setItem("company_id", userAttr?.CompanyID || '');

        const matchedUser = {
          username: `${userAttr?.FirstName} ${userAttr?.LastName}` || userName,
          password: "_",
          attributes: {
            email: userAttr?.EmailAddress,
          },
        };

        console.log("Storing user in local storage...");
        localStorage.setItem("authenticatedUser", JSON.stringify(matchedUser));

        setUser(matchedUser);
        setIsAuthenticated(true);

        toast({
          title: "Signed in successfully",
          description: `Welcome back, ${matchedUser.username}!`,
        });

        navigate("/dashboard");
        return matchedUser;
      } else {
        console.error("Unexpected response:", response);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Unexpected error occurred. Please try again.",
        });
      }
    } catch (error) {
      console.error("Login error:", error);

      if (error.name === "PasswordResetRequiredException") {
        toast({
          variant: "destructive",
          title: "Password Reset Required",
          description: "You need to reset your password. Please check your email for the reset code.",
        });
        return { error: "PasswordResetRequired" };
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Invalid username or password. Please try again.",
        });
      }
    }
  }

  async function verifyMfaCodeForSetup(username: string, code: string) {
    try {
      console.log("Verifying MFA code...");
      const verifyCommand = new VerifySoftwareTokenCommand({
        Session: mfaSession, // Use the current MFA session
        UserCode: code, // The MFA code entered by the user
      });

      const verifyResponse = await cognitoClient.send(verifyCommand);
      console.log("VerifySoftwareToken response:", verifyResponse);

        if(verifyResponse.Status === 'SUCCESS') {
            
            // Show success toast and navigate to the dashboard
            toast({
              title: "Multi-Factor Authentication Setup Successful",
              description: `Please login now to continue`,
            });

            navigate("/dashboard");
        }

    } catch (error) {
      console.error("Error in MFA verification flow:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Invalid MFA code or an error occurred. Please try again.",
      });
    }
  }

  const completeMfaLogin = async (username, mfaCode, session) => {
    const command = new RespondToAuthChallengeCommand({
      ChallengeName: "SOFTWARE_TOKEN_MFA",
      ClientId: config.clientId,
      Session: session, 
      ChallengeResponses: {
        USERNAME: username,
        SOFTWARE_TOKEN_MFA_CODE: mfaCode,
      },
    });
  
    const response = await cognitoClient.send(command);
  
    // If successful, response.AuthenticationResult contains tokens
    return response;
  };

  async function verifyMfaCodeForLogin(username: string, code: string) {
    try {
      const response = await completeMfaLogin(username, code, mfaSession);

      if (response.AuthenticationResult) {

        sessionStorage.setItem("idToken", response.AuthenticationResult.IdToken || '');
        sessionStorage.setItem("accessToken", response.AuthenticationResult.AccessToken || '');
        sessionStorage.setItem("refreshToken", response.AuthenticationResult.RefreshToken || '');

        const userName = await getUserName(response.AuthenticationResult.AccessToken);
        sessionStorage.setItem("user_id", userName || '');
        // Get user details from API
        const userAttr: any = await fetchUser(userName);
        sessionStorage.setItem("given_name", userAttr?.FirstName || '');
        sessionStorage.setItem("family_name", userAttr?.LastName || '');
        sessionStorage.setItem("company_id", userAttr?.CompanyID || '');
        const dashboards = await getAllEmbeddedDashboards();

        const matchedUser = {
          username: `${userAttr?.FirstName} ${userAttr?.LastName}` || userName,
          password: "_",
          attributes: {
            email: userAttr?.EmailAddress,
          }
        };

        // Store user in local storage
        localStorage.setItem(
          "authenticatedUser",
          JSON.stringify(matchedUser)
        );

        setUser(matchedUser);
        setIsAuthenticated(true);
        setMfaRequired(false);
        setMfaSession(null);

        toast({
          title: "MFA Verified successfully",
          description: `Welcome back, ${matchedUser.username}!`,
        });

        navigate("/dashboard");
      }
      else{
        console.error("Unexpected response:", response);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Unexpected error occurred in login. Please try again.",
        });
      }
    } catch (error) {
      console.error("MFA verification error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Invalid MFA code. Please try again.",
      });
    }
  }

  async function signOut() {
    try {
      // Clear the user from localStorage
      localStorage.removeItem("authenticatedUser");

      setUser(null);
      setIsAuthenticated(false);

      toast({
        title: "Signed out successfully",
      });

      navigate("/login");
    } catch (error) {
      if (error instanceof Error) {
        toast({
          variant: "destructive",
          title: "Sign out failed",
          description: error.message,
        });
      }
      console.error("Error signing out: ", error);
    }
  }

  async function changePasswordBySession(username: string, password: string) {
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: config.clientId,
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: password,
        },
      });

      const response = await cognitoClient.send(command);
      console.log("Password reset response:", response);

      toast({
        title: "Password Reset Successful",
        description: "You can now log in with your new password.",
      });
      navigate("/login");
    } catch (error) {
      console.error("Password reset error:", error);
      throw new Error("Failed to reset password. Please try again.");
    }
  }

  async function resetPassword(username: string, resetCode: string, newPassword: string) {
    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: config.clientId,
        Username: username,
        ConfirmationCode: resetCode,
        Password: newPassword,
      });

      const response = await cognitoClient.send(command);
      console.log("Password reset response:", response);
      navigate("/login");
      toast({
        title: "Password Reset Successful",
        description: "You can now log in with your new password.",
      });
    } catch (error) {
      console.error("Password reset error:", error);
      throw new Error("Failed to reset password. Please try again.");
    }
  }

  async function setUserMfaPreference(accessToken: string) {
    try {
      const command = new SetUserMFAPreferenceCommand({
        AccessToken: session, // Use the session instead of accessToken
        SoftwareTokenMfaSettings: {
          Enabled: true, // Enable the Authenticator App
          PreferredMfa: true, // Set it as the preferred MFA method
        },
      });

      const response = await cognitoClient.send(command);
      console.log("MFA preference set successfully:", response);

      toast({
        title: "MFA Preference Updated",
        description: "Authenticator App is now the preferred MFA method.",
      });
    } catch (error) {
      console.error("Error setting MFA preference:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to set MFA preference. Please try again.",
      });
      throw error; // Ensure the error is propagated if setting MFA preference fails
    }
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        signIn,
        signOut,
        verifyMfaCodeForSetup,
        mfaRequired,
        verifyMfaCodeForLogin,
        resetPassword,
        changePasswordBySession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const getUserName = async (accessToken: string) => {
  const input = { // GetUserRequest
    AccessToken: accessToken,
  };
  const command = new GetUserCommand(input);
  const response = await cognitoClient.send(command);
  console.log("🚀 ~ getUser ~ response:", response);

  return response.Username;
};

// Retrieve user details from API
const fetchUser = async (userName: string) => {
  console.log("fetch user ", userName);

  const headers = await getHeaders();
  try {
    console.log("Fetching user details from API...");
    console.log("API URL:", API_URL);
    console.log("Vite API URL:", import.meta.env.VITE_API_URL);
    console.log("vite xero id", import.meta.env.VITE_XERO_ID);
    const response = await fetch(`${API_URL}/user/${userName}`, {
      mode: 'cors',
      method: 'GET',
      headers: headers,
    });
    const data = await response.json();

    console.log("🚀 ~ fetchUser ~ response:", data);
    return data;
  } catch (error) {
    console.error("Error:", error);
  }
};

export async function getHeaders(): Promise<HeadersInit> {
  try {
    let accessToken = sessionStorage.getItem("accessToken");
    const validJwt = await validateAccessToken(accessToken);
    if (!validJwt) {
      accessToken = await refreshAccessToken();
    }
    const headers: HeadersInit = {
      'Accept': 'application/json',
      'Content-Type': 'text/plain',
      'Authorization': `Bearer ${accessToken}`,
    };

    return headers;
  } catch (error) {
    console.error('Error getting Headers:', error);
    throw error;
  }
}

export const validateAccessToken = async (accessToken: string) => {
  try {
    const decodedToken = jwtDecode<JwtPayload>(accessToken);
    if (decodedToken && typeof decodedToken === 'object') {
      const { exp } = decodedToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (exp && currentTime <= exp) {
        // Token is valid and not expired
        return true;
      }
    }
    // Token is invalid or expired
    return false;
  } catch (error) {
    console.error('Error verifying access token:', error);
    throw error;
  }
};

export const refreshAccessToken = async () => {
  try {
    const refreshToken = sessionStorage.getItem("refreshToken");
    const params: InitiateAuthCommandInput = {
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: config.clientId,
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
    };
    const command = new InitiateAuthCommand(params);
    const { AuthenticationResult } = await cognitoClient.send(command);
    if (AuthenticationResult) {
      sessionStorage.setItem("accessToken", AuthenticationResult.AccessToken || '');
      sessionStorage.setItem("refreshToken", AuthenticationResult.RefreshToken || '');
      return AuthenticationResult.AccessToken;
    }
  }
  catch (error) {
    console.error("Error refreshing token");
  }
};

export const getAllEmbeddedDashboards = async () => {
  const headers = await getHeaders();
  try {
    const fetchedPost = await fetch(`${API_URL}/analytics/getAllDashboards`, {
      mode: 'cors',
      method: 'get',
      headers: headers,
    });
    const data = await fetchedPost.json();
    console.log("embedded dashboards ", data);
    sessionStorage.setItem("dashboards", JSON.stringify(data));
    return data;
  } catch (error) {
    console.error("Error:", error);
    return "Error getting embedded dashboards";
  }
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
